import { useCallback, useMemo } from 'react'

import { useConfigurationContext } from '../../context/configurationContext'
import { useTemplateContext } from '../../context/templateContext'
import {
  areVersionsEqual,
  type TemplateVersionInfo,
} from '../../types/templateModel'
import { useInheritedTemplateVersionInfos } from '../../uiModels/versionInfo/useInheritedTemplateVersionInfos'
import { activeVersionForDate } from './helpers'

export const useIsTemplateVersionActive = (
  timelineVersionIdentifier: string,
) => {
  const { viewAt } = useConfigurationContext()
  const { templateVersion } = useTemplateContext()
  const inheritedVersions = useInheritedTemplateVersionInfos(
    timelineVersionIdentifier,
  )
  const inheritedVersionsWithFromDate = useMemo(
    () =>
      inheritedVersions
        // We skip versions without a from date since those match up against the initial versions of all templates.
        .filter((versionInfo) => !!versionInfo?.fromDate),
    [inheritedVersions],
  )
  const activeInheritedVersion = useMemo(
    () => activeVersionForDate(inheritedVersionsWithFromDate, viewAt),
    [inheritedVersionsWithFromDate, viewAt],
  )

  return useCallback(
    (version: TemplateVersionInfo) => {
      const isCurrentTemplateVersion =
        !!templateVersion?.versionInfo &&
        areVersionsEqual(templateVersion.versionInfo, version)

      const isActiveInheritedVersion =
        !!activeInheritedVersion &&
        areVersionsEqual(version, activeInheritedVersion)
      const isVersionsLineForCurrentTemplateVersion =
        !!templateVersion &&
        timelineVersionIdentifier === templateVersion.versionInfo.identifier

      return (
        isCurrentTemplateVersion ||
        (isActiveInheritedVersion && isVersionsLineForCurrentTemplateVersion)
      )
    },
    [activeInheritedVersion, templateVersion, timelineVersionIdentifier],
  )
}
